import { Flex, IconButton, SystemStyleObject, Tooltip } from '@chakra-ui/react';
import { CancelIcon, CheckedIcon, CircleEmptyIcon } from '@udacity/chakra-uds-icons';
import { useAppToast } from 'app/common/hooks/use-app-toast';
import { MeetingAttendance, MeetingAttendanceStatus } from 'app/types/uconnect';
import _chunk from 'lodash/chunk';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateAttendancesMutation } from '../queries/enhanced';
import { getAttendanceErrorMessage } from '../helpers/utils';

type BulkAttendanceButtonsProps = {
  meetingId: string;
  selectedUserKeys: string[];
  onUpdated?: () => void;
};

const BULK_UPDATE_BATCH_SIZE = 200;

const STATUS_LABEL_KEY: Partial<Record<MeetingAttendanceStatus, string>> = {
  [MeetingAttendanceStatus.Attended]: 'connect.attended',
  [MeetingAttendanceStatus.Absent]: 'connect.absent',
  [MeetingAttendanceStatus.Excused]: 'connect.excused',
};

export const BulkAttendanceButtons: React.FC<BulkAttendanceButtonsProps> = ({
  meetingId,
  selectedUserKeys,
  onUpdated,
}) => {
  const { t } = useTranslation();
  const toast = useAppToast();
  const [updateAttendances] = useUpdateAttendancesMutation();
  const [loadingStatus, setLoadingStatus] = useState<MeetingAttendanceStatus | null>(null);

  async function handleUpdateAttendances(status: MeetingAttendanceStatus) {
    const batches = _chunk(selectedUserKeys, BULK_UPDATE_BATCH_SIZE);
    setLoadingStatus(status);
    try {
      const results = await Promise.all(
        batches.map((batch) => {
          const attendances: MeetingAttendance[] = batch.map((userKey) => ({ userKey, meetingId, status }));
          return updateAttendances({ attendances });
        })
      );
      const failed = results.find((res) => 'error' in res);
      if (failed && 'error' in failed) {
        toast({
          variant: 'error',
          description: getAttendanceErrorMessage(failed.error, t),
        });
      } else {
        toast({
          variant: 'success',
          description: t('connect.bulkAttendanceUpdated', {
            count: selectedUserKeys.length,
            status: t(STATUS_LABEL_KEY[status] ?? 'connect.notRecorded'),
          }),
        });
        onUpdated?.();
      }
    } finally {
      setLoadingStatus(null);
    }
  }

  const sharedButtonProps = (
    status: MeetingAttendanceStatus
  ): {
    isRound: boolean;
    boxSize: number;
    isLoading: boolean;
    isDisabled: boolean;
    onClick: () => void;
    sx: SystemStyleObject;
  } => ({
    isRound: true,
    boxSize: 12,
    isLoading: loadingStatus === status,
    isDisabled: !selectedUserKeys.length || loadingStatus !== null,
    onClick: () => handleUpdateAttendances(status),
    // Chakra v1 positions the loading spinner at the start of the button; with
    // no button text that leaves it off-center, so center it explicitly.
    sx: {
      '& .chakra-button__spinner': {
        position: 'absolute',
        insetStart: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        margin: 0,
      },
    },
  });

  return (
    <Flex gap={6}>
      <Tooltip label={t('connect.attended')}>
        <IconButton
          {...sharedButtonProps(MeetingAttendanceStatus.Attended)}
          colorScheme="green"
          icon={<CheckedIcon h={8} w={8} />}
          aria-label={t('connect.setAttended')}
        />
      </Tooltip>
      <Tooltip label={t('connect.excused')}>
        <IconButton
          {...sharedButtonProps(MeetingAttendanceStatus.Excused)}
          colorScheme="gray"
          bg="gray.600"
          color="white"
          _hover={{ bg: 'gray.700' }}
          _active={{ bg: 'gray.800' }}
          icon={<CircleEmptyIcon h={8} w={8} />}
          aria-label={t('connect.setExcused')}
        />
      </Tooltip>
      <Tooltip label={t('connect.absent')}>
        <IconButton
          {...sharedButtonProps(MeetingAttendanceStatus.Absent)}
          colorScheme="red"
          icon={<CancelIcon h={8} w={8} />}
          aria-label={t('connect.setAbsent')}
        />
      </Tooltip>
    </Flex>
  );
};
